/**
 * Global React Query client + hooks for rarely-changing reference data.
 * Cuts redundant API calls for tour_operators / branches / users / employees by ~80%.
 */
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  tourOperatorsAPI, branchesAPI, usersAPI, clientsAPI,
} from '../services/api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Rarely-changing reference data — keep cached for 60s, no auto-refetch on focus
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

// Cache keys (centralized)
export const QK = {
  tourOperators: ['tour-operators'],
  branches: ['branches'],
  employees: ['employees'],
  users: ['users'],
  clients: (params) => ['clients', params || {}],
  dashboardStats: ['dashboard', 'stats'],
};

// ===== Reference data (TTL 60s, shared across pages) =====

export const useTourOperators = () => useQuery({
  queryKey: QK.tourOperators,
  queryFn: async () => (await tourOperatorsAPI.getAll()).data,
});

export const useBranches = () => useQuery({
  queryKey: QK.branches,
  queryFn: async () => {
    try { return (await branchesAPI.getAll()).data; } catch { return []; }
  },
});

export const useEmployees = () => useQuery({
  queryKey: QK.employees,
  queryFn: async () => {
    try { return (await usersAPI.getAll()).data; } catch { return []; }
  },
});

export const useUsers = () => useQuery({
  queryKey: QK.users,
  queryFn: async () => {
    try { return (await usersAPI.getAll()).data; } catch { return []; }
  },
});

// Clients - shorter TTL (30s) since they change more often
export const useClients = (params) => useQuery({
  queryKey: QK.clients(params),
  queryFn: async () => (await clientsAPI.getAll(params)).data,
  staleTime: 30 * 1000,
});

// Invalidation helpers
export const invalidateClients = () => queryClient.invalidateQueries({ queryKey: ['clients'] });
export const invalidateBranches = () => queryClient.invalidateQueries({ queryKey: QK.branches });
export const invalidateTourOperators = () => queryClient.invalidateQueries({ queryKey: QK.tourOperators });
